<template>
  <aside class="sidebar-left">

    <!-- تبلیغات -->
    <div class="ad-section">
      <img src="@/assets/images/banner.png" class="ad-placeholder" alt="banner">
    </div>

    <!-- ویجت جدول لیگ‌ها (مثل ورزش۳) -->
    <div class="widget">
      <h3 class="sidebar-title">
        <i class="fas fa-table"></i>
        جدول لیگ‌ها
      </h3>

      <select
          v-model="tableLeagueId"
          class="league-select"
          @change="loadTable"
      >
        <option
            v-for="lg in allLeagues"
            :key="'t-' + lg.id"
            :value="lg.id"
        >
          {{ leagueLabel(lg) }}
        </option>
      </select>

      <div v-if="tableLoading" class="widget-loading">
        <div class="spinner"></div>
      </div>

      <template v-else-if="tableRows.length">
        <div class="mini-table">
          <div class="mini-table-header">
            <span class="col-pos">#</span>
            <span class="col-team">تیم</span>
            <span class="col-num">بازی</span>
            <span class="col-num">امتیاز</span>
          </div>

          <router-link
              v-for="row in tableRows"
              :key="row.slug || row.name"
              class="mini-table-row"
              :to="`/competition/${tableLeagueId}`"
          >
            <span class="col-pos" :class="posClass(row.position)">{{ row.position }}</span>
            <span class="col-team">
              <img
                  v-if="row.logo"
                  :src="row.logo"
                  :alt="row.name"
                  class="team-logo"
              >
              <span class="team-name">{{ faTeam(row.name) }}</span>
            </span>
            <span class="col-num muted">{{ row.played }}</span>
            <span class="col-num points">{{ row.points }}</span>
          </router-link>
        </div>

        <router-link
            :to="`/competition/${tableLeagueId}`"
            class="widget-more"
        >
          جدول کامل <i class="fas fa-angle-left"></i>
        </router-link>
      </template>

      <div v-else class="widget-empty">
        <i class="fas fa-table"></i>
        جدولی برای این لیگ ثبت نشده
      </div>
    </div>

    <!-- ویجت برنامه بازی‌ها (مثل ورزش۳) -->
    <div class="widget">
      <h3 class="sidebar-title">
        <i class="far fa-calendar-alt"></i>
        برنامه بازی‌ها
      </h3>

      <select
          v-model="fixturesLeagueId"
          class="league-select"
          @change="loadFixtures"
      >
        <option
            v-for="lg in allLeagues"
            :key="'f-' + lg.id"
            :value="lg.id"
        >
          {{ leagueLabel(lg) }}
        </option>
      </select>

      <div v-if="fixturesLoading" class="widget-loading">
        <div class="spinner"></div>
      </div>

      <template v-else-if="groupedFixtures.length">
        <div class="fixtures-mode-label" :class="{ results: fixturesAreResults }">
          <i :class="fixturesAreResults ? 'fas fa-clipboard-check' : 'far fa-clock'"></i>
          {{ fixturesAreResults ? 'نتایج آخرین بازی‌ها' : 'بازی‌های پیشِ رو' }}
        </div>

        <div
            v-for="group in groupedFixtures"
            :key="group.key"
            class="fixture-day"
        >
          <div class="fixture-day-label">{{ group.label }}</div>

          <div
              v-for="m in group.matches"
              :key="m.id"
              class="fixture-item"
              @click="$router.push(`/match/${m.id}`)"
          >
            <div class="fixture-row">
              <span class="f-team">
                <img
                    v-if="m.home?.team?.logo"
                    :src="m.home.team.logo"
                    :alt="m.home.team.name"
                >
                <span>{{ faTeam(m.home?.team?.name) }}</span>
              </span>

              <span class="f-mid" :class="midClass(m)">
                {{ midText(m) }}
              </span>

              <span class="f-team away">
                <span>{{ faTeam(m.away?.team?.name) }}</span>
                <img
                    v-if="m.away?.team?.logo"
                    :src="m.away.team.logo"
                    :alt="m.away.team.name"
                >
              </span>
            </div>
          </div>
        </div>

        <router-link
            :to="`/competition/${fixturesLeagueId}`"
            class="widget-more"
        >
          همهٔ بازی‌ها <i class="fas fa-angle-left"></i>
        </router-link>
      </template>

      <div v-else class="widget-empty">
        <i class="far fa-futbol"></i>
        بازی‌ای برای این لیگ ثبت نشده
      </div>
    </div>

  </aside>
</template>

<script>
import apiService from '../services/api'
import { leagueNames, leagueNamesLatin, teamNameFa } from '@/utils/translations'

const TABLE_ROW_LIMIT = 8
const FIXTURES_LIMIT = 6

export default {
  name: 'SidebarLeft',

  data() {
    return {
      fallbackLeagues: [],
      tableLeagueId: null,
      fixturesLeagueId: null,
      tableRows: [],
      fixtures: [],
      fixturesAreResults: false,
      tableLoading: false,
      fixturesLoading: false,
      tableCache: {},     // کلید: leagueId → سطرهای جدول
      fixturesCache: {},  // کلید: leagueId → { upcoming, results }
    }
  },

  computed: {
    // لیگ‌ها از استور (App.vue لود می‌کند) — لیگ برتر ایران خودکار اول است
    leagues() {
      return this.$store.getters.competitions || []
    },

    allLeagues() {
      return this.leagues.length ? this.leagues : this.fallbackLeagues
    },

    // گروه‌بندی بازی‌ها بر اساس روز (مثل ورزش۳)
    groupedFixtures() {
      const groups = []
      const map = {}
      for (const m of this.fixtures) {
        const key = this.dayKey(m.kickoffAt)
        if (!map[key]) {
          map[key] = { key, label: this.dayLabel(m.kickoffAt), matches: [] }
          groups.push(map[key])
        }
        map[key].matches.push(m)
      }
      return groups
    },
  },

  watch: {
    // وقتی لیست لیگ‌ها از استور رسید، مقدار پیش‌فرض را بگذار و لود کن
    allLeagues(list) {
      if (list.length && !this.tableLeagueId) {
        this.initDefaults()
      }
    },
  },

  async mounted() {
    // اگر استور خالی بود (خطای API یا تأخیر)، مستقیم از سرور می‌گیریم
    if (!this.leagues.length) {
      try {
        const res = await apiService.getTopLeagues()
        this.fallbackLeagues = Array.isArray(res) ? res : []
      } catch (e) {
        console.error('SidebarLeft: leagues fetch failed', e)
      }
    }
    if (this.allLeagues.length) {
      this.initDefaults()
    }
  },

  methods: {
    initDefaults() {
      if (!this.allLeagues.length) return
      // لیگ برتر ایران اول لیست است (اولویت بک‌اند) → پیش‌فرض همان
      const firstId = this.allLeagues[0].id
      if (!this.tableLeagueId) {
        this.tableLeagueId = firstId
        this.loadTable()
      }
      if (!this.fixturesLeagueId) {
        this.fixturesLeagueId = firstId
        this.loadFixtures()
      }
    },

    leagueLabel(lg) {
      return leagueNames[lg.shortName] || leagueNamesLatin[lg.name] || lg.name
    },

    faTeam(name) {
      return teamNameFa(name) || 'نامشخص'
    },

    async loadTable() {
      const id = this.tableLeagueId
      if (!id) return

      if (this.tableCache[id]) {
        this.tableRows = this.tableCache[id]
        return
      }

      this.tableLoading = true
      this.tableRows = []
      try {
        const rows = await apiService.request(`/competition/${id}/table`)
        const sliced = (rows || []).slice(0, TABLE_ROW_LIMIT)
        this.tableCache[id] = sliced
        if (this.tableLeagueId === id) this.tableRows = sliced
      } catch (e) {
        console.error('SidebarLeft: table fetch failed', e)
      } finally {
        this.tableLoading = false
      }
    },

    async loadFixtures() {
      const id = this.fixturesLeagueId
      if (!id) return

      if (this.fixturesCache[id]) {
        this.applyFixtures(this.fixturesCache[id])
        return
      }

      this.fixturesLoading = true
      this.fixtures = []
      try {
        const res = await apiService.request(
            `/matches/fixtures?competitionId=${id}&take=${FIXTURES_LIMIT}`
        )
        const data = { upcoming: res?.upcoming || [], results: res?.results || [] }
        this.fixturesCache[id] = data
        if (this.fixturesLeagueId === id) this.applyFixtures(data)
      } catch (e) {
        console.error('SidebarLeft: fixtures fetch failed', e)
      } finally {
        this.fixturesLoading = false
      }
    },

    applyFixtures(data) {
      // اولویت با بازی‌های پیشِ روست؛ اگر فصل تمام شده باشد نتایج آخر نمایش داده می‌شود
      if (data.upcoming.length) {
        this.fixtures = data.upcoming
        this.fixturesAreResults = false
      } else {
        this.fixtures = data.results
        this.fixturesAreResults = true
      }
    },

    // ===== نمایش وسط ردیف بازی =====
    isFinished(m) {
      return m.status === 'FINISHED'
    },

    isLive(m) {
      return ['LIVE', 'IN_PLAY', 'PAUSED'].includes(m.status)
    },

    midText(m) {
      if (this.isFinished(m)) {
        const h = Number(m.home?.score ?? 0).toLocaleString('fa-IR')
        const a = Number(m.away?.score ?? 0).toLocaleString('fa-IR')
        return `${h} - ${a}`
      }
      if (this.isLive(m)) return 'در جریان'
      return this.faTime(m.kickoffAt)
    },

    midClass(m) {
      return {
        finished: this.isFinished(m),
        live: this.isLive(m),
      }
    },

    faTime(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleTimeString('fa-IR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    // ===== گروه‌بندی روزها =====
    // کلید روز بر اساس تاریخ محلی مرورگر (تهران) — هم‌راستا با برچسب روزها
    dayKey(dateStr) {
      const d = new Date(dateStr)
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    },

    dayLabel(dateStr) {
      const d = new Date(dateStr)
      const now = new Date()
      const todayKey = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
      const dayKey = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
      const diffDays = Math.round((dayKey - todayKey) / 86400000)

      if (diffDays === 0) return 'امروز'
      if (diffDays === 1) return 'فردا'
      if (diffDays === -1) return 'دیروز'
      return d.toLocaleDateString('fa-IR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    },

    posClass(pos) {
      if (pos <= 3) return 'pos-good'
      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/sass/variables';
@import '@/assets/sass/mixin';

.sidebar-left {
  width: $sidebar-width;
  background: $white;
  border-radius: $border-radius;
  padding: 15px;
  box-shadow: $shadow-md;
  flex-shrink: 0;
}

.ad-section {
  margin-bottom: 25px;
}

.ad-placeholder {
  background: $gray-100;
  height: 200px;
  width: 300px;
  max-width: 100%;
  @include flex-center;
  color: $gray-500;
  border-radius: $border-radius;
}

.widget {
  margin-bottom: 25px;
}

.sidebar-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $primary-blue;
  font-size: 1rem;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid $gray-200;
}

/* ===== سلکت لیگ ===== */
.league-select {
  width: 100%;
  padding: 8px 10px;
  margin-bottom: 10px;
  border: 1px solid $gray-200;
  border-radius: $border-radius-sm;
  background: $gray-50;
  color: $gray-700;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: $secondary-blue;
  }
}

/* ===== حالت لودینگ و خالی ===== */
.widget-loading {
  padding: 30px 0;
  text-align: center;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid $gray-200;
  border-top-color: $primary-blue;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.widget-empty {
  text-align: center;
  padding: 26px 10px;
  color: $gray-400;
  font-size: 0.82rem;

  i {
    display: block;
    font-size: 1.6rem;
    margin-bottom: 8px;
  }
}

.widget-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 10px;
  padding: 8px;
  border-radius: $border-radius-sm;
  background: $gray-50;
  color: $primary-blue;
  font-size: 0.8rem;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: $gray-100;
  }
}

/* ===== مینی‌جدول ===== */
.mini-table {
  border: 1px solid $gray-200;
  border-radius: $border-radius-sm;
  overflow: hidden;
}

.mini-table-header {
  display: grid;
  grid-template-columns: 28px 1fr 36px 44px;
  align-items: center;
  padding: 8px 10px;
  background: $gray-50;
  font-weight: 700;
  font-size: 0.78rem;
  color: $gray-600;
  border-bottom: 1px solid $gray-200;
}

.mini-table-row {
  display: grid;
  grid-template-columns: 28px 1fr 36px 44px;
  align-items: center;
  padding: 7px 10px;
  border-bottom: 1px solid $gray-100;
  text-decoration: none;
  color: inherit;
  transition: background 0.2s;

  &:hover {
    background: $gray-50;
  }

  &:last-child {
    border-bottom: none;
  }
}

.col-pos {
  font-size: 0.75rem;
  font-weight: 700;
  color: $gray-500;

  &.pos-good {
    color: #16a34a;
  }
}

.col-team {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.team-logo {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
}

.team-name {
  font-size: 0.82rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.col-num {
  text-align: center;
  font-size: 0.8rem;

  &.muted {
    color: $gray-500;
  }

  &.points {
    font-weight: 700;
    color: $primary-blue;
  }
}

/* ===== برنامه بازی‌ها ===== */
.fixtures-mode-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  color: $secondary-blue;
  margin-bottom: 8px;

  &.results {
    color: #16a34a;
  }
}

.fixture-day {
  margin-bottom: 8px;
}

.fixture-day-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: $gray-500;
  background: $gray-50;
  border-radius: 4px;
  padding: 4px 8px;
  margin-bottom: 4px;
  text-align: center;
}

.fixture-item {
  cursor: pointer;
  border-radius: $border-radius-sm;
  transition: background 0.2s;

  &:hover {
    background: $gray-50;
  }
}

.fixture-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 6px;
  padding: 7px 6px;
  border-bottom: 1px solid $gray-100;
}

.f-team {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 0.8rem;
  font-weight: 500;

  &.away {
    justify-content: flex-end;
    text-align: left;
  }

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    flex-shrink: 0;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.f-mid {
  font-size: 0.78rem;
  font-weight: 700;
  color: $gray-600;
  white-space: nowrap;
  direction: ltr;

  &.live {
    color: $red;
    animation: pulse 1.2s infinite;
  }

  &.finished {
    color: $gray-800;
  }
}

@keyframes pulse {
  50% {
    opacity: 0.5;
  }
}

/* ===== موبایل ===== */
@include mobile {
  .sidebar-left {
    width: 100%;
    margin: 10px 0;
  }

  .ad-placeholder {
    height: 150px;
  }

  .fixture-row {
    padding: 9px 6px;
  }
}

/* ===== تبلت ===== */
@include tablet {
  .sidebar-left {
    width: 280px;
  }
}
</style>
